import { SupabaseClient, RealtimeChannel } from "@supabase/supabase-js";
import { loggerService } from "../logger-service";
import { _supabaseClient } from "./supabase-instance";

// 🚀 Connection Pool Manager Types
export interface SubscriptionConfig {
  channelName: string;
  table?: string;
  event?: string;
  filter?: string;
  callback: (payload: any) => void;
  broadcastEvent?: string;
}

export interface ChannelRegistry {
  [channelId: string]: {
    channel: RealtimeChannel;
    subscriptions: Map<string, SubscriptionConfig>;
    isActive: boolean;
  };
}

export enum SubscriptionType {
  POSTGRES_CHANGES = "postgres_changes",
  BROADCAST = "broadcast",
}

// 🚀 Unified Connection Pool Manager
export class SupabaseConnectionPoolManager {
  private static instance: SupabaseConnectionPoolManager;
  private supabase: SupabaseClient;
  private channels: ChannelRegistry = {};
  private messageQueue: Array<{ channelId: string; payload: any }> = [];
  private processingQueue = false;

  private constructor() {
    this.supabase = _supabaseClient;
    loggerService.log("🚀 Connection Pool Manager initialized");
  }

  // Singleton pattern
  public static getInstance(): SupabaseConnectionPoolManager {
    if (!SupabaseConnectionPoolManager.instance) {
      SupabaseConnectionPoolManager.instance =
        new SupabaseConnectionPoolManager();
    }
    return SupabaseConnectionPoolManager.instance;
  }

  // 🚀 Subscribe to postgres changes with unified channel management
  public subscribeToPostgresChanges(
    subscriptionId: string,
    config: SubscriptionConfig
  ): string {
    const channelId = this.generateChannelId(config.channelName, config.table);

    // Create or reuse channel
    if (!this.channels[channelId]) {
      this.createChannel(channelId, config.channelName);
    }

    // Add subscription to channel
    this.channels[channelId].subscriptions.set(subscriptionId, config);

    // Setup postgres changes listener if not already set
    if (!this.channels[channelId].isActive) {
      this.setupPostgresListener(channelId, config);
    }

    loggerService.log(
      `📡 Subscription added: ${subscriptionId} -> ${channelId}`
    );
    return channelId;
  }

  // 🚀 Subscribe to broadcast events with unified channel management
  public subscribeToBroadcast(
    subscriptionId: string,
    config: SubscriptionConfig
  ): string {
    const channelId = this.generateChannelId(config.channelName);

    // Create or reuse channel
    if (!this.channels[channelId]) {
      this.createChannel(channelId, config.channelName);
    }

    // Add subscription to channel
    this.channels[channelId].subscriptions.set(subscriptionId, config);

    // Setup broadcast listener if not already set
    if (!this.channels[channelId].isActive) {
      this.setupBroadcastListener(channelId, config);
    }

    loggerService.log(
      `📢 Broadcast subscription added: ${subscriptionId} -> ${channelId}`
    );
    return channelId;
  }

  // 🚀 Unsubscribe from specific subscription
  public unsubscribe(subscriptionId: string, channelId: string): void {
    const channelRegistry = this.channels[channelId];

    if (!channelRegistry) {
      loggerService.log(`⚠️ Channel not found: ${channelId}`);
      return;
    }

    // Remove subscription
    channelRegistry.subscriptions.delete(subscriptionId);

    // If no more subscriptions, close channel
    if (channelRegistry.subscriptions.size === 0) {
      this.closeChannel(channelId);
    }

    loggerService.log(`🔌 Unsubscribed: ${subscriptionId} from ${channelId}`);
  }

  // 🚀 Get connection statistics
  public getStats() {
    const stats = {
      totalChannels: Object.keys(this.channels).length,
      activeChannels: Object.values(this.channels).filter((c) => c.isActive)
        .length,
      totalSubscriptions: Object.values(this.channels).reduce(
        (total, channel) => total + channel.subscriptions.size,
        0
      ),
      queueSize: this.messageQueue.length,
    };

    loggerService.log("📊 Connection Pool Stats:", stats);
    return stats;
  }

  // 🚀 Private helper methods
  private generateChannelId(channelName: string, table?: string): string {
    return table ? `${channelName}_${table}` : channelName;
  }

  private createChannel(channelId: string, channelName: string): void {
    const channel = this.supabase.channel(channelName);

    this.channels[channelId] = {
      channel,
      subscriptions: new Map(),
      isActive: false,
    };
  }

  private setupPostgresListener(
    channelId: string,
    config: SubscriptionConfig
  ): void {
    const channelRegistry = this.channels[channelId];

    const postgresConfig = {
      event: config.event || "*",
      schema: "public",
      table: config.table!,
      ...(config.filter && { filter: config.filter }),
    };

    channelRegistry.channel
      .on("postgres_changes" as any, postgresConfig, (payload: any) => {
        this.routeMessage(channelId, payload);
      })
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          channelRegistry.isActive = true;
          loggerService.log(`✅ Postgres channel active: ${channelId}`);
        } else if (status === "CHANNEL_ERROR") {
          loggerService.log(`❌ Channel error: ${channelId}`);
          this.handleChannelError(channelId);
        }
      });
  }

  private setupBroadcastListener(
    channelId: string,
    config: SubscriptionConfig
  ): void {
    const channelRegistry = this.channels[channelId];

    // Kanal hâlihazırda subscribe edilmişse yeniden deneme
    if (!channelRegistry) return;
    if (channelRegistry.isActive) return;
    if (
      channelRegistry.channel.state === "joined" ||
      channelRegistry.channel.state === "joining"
    ) {
      channelRegistry.isActive = true;
      return;
    }

    channelRegistry.channel
      .on(
        "broadcast",
        { event: config.broadcastEvent || "*" },
        (payload: any) => {
          this.routeMessage(channelId, payload);
        }
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          channelRegistry.isActive = true;
          loggerService.log(`✅ Broadcast channel active: ${channelId}`);
        } else if (status === "CHANNEL_ERROR") {
          loggerService.log(`❌ Broadcast channel error: ${channelId}`);
          this.handleChannelError(channelId);
        }
      });
  }

  // 🚀 Message routing to all subscriptions on a channel
  private routeMessage(channelId: string, payload: any): void {
    const channelRegistry = this.channels[channelId];

    if (!channelRegistry) return;

    // Add to queue for batch processing
    this.messageQueue.push({ channelId, payload });
    this.processMessageQueue();
  }

  // 🚀 Batch process messages for better performance
  private async processMessageQueue(): Promise<void> {
    if (this.processingQueue || this.messageQueue.length === 0) return;

    this.processingQueue = true;

    try {
      const batch = this.messageQueue.splice(0, 10); // Process 10 at a time

      batch.forEach(({ channelId, payload }) => {
        const channelRegistry = this.channels[channelId];

        if (channelRegistry) {
          channelRegistry.subscriptions.forEach((config) => {
            try {
              config.callback(payload);
            } catch (error) {
              loggerService.log(`❌ Callback error for ${channelId}:`, error);
            }
          });
        }
      });

      // Process remaining messages
      if (this.messageQueue.length > 0) {
        setTimeout(() => this.processMessageQueue(), 16); // ~60fps
      }
    } finally {
      this.processingQueue = false;
    }
  }

  private closeChannel(channelId: string): void {
    const channelRegistry = this.channels[channelId];

    if (channelRegistry) {
      channelRegistry.channel.unsubscribe();
      delete this.channels[channelId];
      loggerService.log(`🔒 Channel closed: ${channelId}`);
    }
  }

  private handleChannelError(channelId: string): void {
    const channelRegistry = this.channels[channelId];

    if (channelRegistry) {
      channelRegistry.isActive = false;

      // Retry connection after delay
      setTimeout(() => {
        if (this.channels[channelId]) {
          loggerService.log(`🔄 Retrying connection: ${channelId}`);
          // Implementation for retry logic would go here
        }
      }, 5000);
    }
  }

  // 🚀 Clean shutdown
  public shutdown(): void {
    Object.keys(this.channels).forEach((channelId) => {
      this.closeChannel(channelId);
    });

    this.messageQueue = [];
    loggerService.log("🔌 Connection Pool Manager shutdown");
  }
}

// Export singleton instance
export const connectionPoolManager =
  SupabaseConnectionPoolManager.getInstance();
